import { Component } from '@theme/component';

const MAIN_ID = 'MainContent';
const TAB_SELECTOR = 'a[data-role-tab]';

const pages = new Map();
let navigationId = 0;
let renderedPath = window.location.pathname;

/**
 * @param {string} url
 * @returns {Promise<string>}
 */
function loadPage(url) {
  if (!pages.has(url)) {
    const request = (async () => {
      const response = await fetch(url, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Role page request failed with ${response.status}`);
      return response.text();
    })();

    request.catch(() => pages.delete(url));
    pages.set(url, request);
  }

  return /** @type {Promise<string>} */ (pages.get(url));
}

/**
 * @param {HTMLElement} container
 */
function activateScripts(container) {
  for (const script of container.querySelectorAll('script')) {
    const fresh = document.createElement('script');

    for (const { name, value } of script.attributes) fresh.setAttribute(name, value);
    fresh.textContent = script.textContent;
    script.replaceWith(fresh);
  }
}

/**
 * @param {HTMLElement} main
 */
function keepTabsInView(main) {
  const tabs = main.querySelector('role-tabs');
  if (!(tabs instanceof HTMLElement)) return;

  const { top } = tabs.getBoundingClientRect();
  if (top < 0) tabs.scrollIntoView({ block: 'start' });
}

/**
 * @param {string} url
 * @param {{ push: boolean }} options
 */
async function navigate(url, { push }) {
  const main = document.getElementById(MAIN_ID);
  if (!main) {
    window.location.assign(url);
    return;
  }

  const currentNavigation = ++navigationId;
  main.setAttribute('aria-busy', 'true');

  try {
    const html = await loadPage(url);
    if (currentNavigation !== navigationId) return;

    const nextDocument = new DOMParser().parseFromString(html, 'text/html');
    const nextMain = nextDocument.getElementById(MAIN_ID);
    if (!nextMain) throw new Error('Role page response has no main content');

    const swap = () => {
      main.replaceChildren(...Array.from(nextMain.childNodes, (node) => document.importNode(node, true)));
      if (nextMain.dataset.template) main.dataset.template = nextMain.dataset.template;
      activateScripts(main);
    };

    if (push) window.history.pushState({ roleTabs: true }, '', url);
    renderedPath = new URL(url, window.location.origin).pathname;
    document.title = nextDocument.title;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (document.startViewTransition && !reduceMotion) {
      await document.startViewTransition(swap).updateCallbackDone;
    } else {
      swap();
    }

    keepTabsInView(main);

    const activeTab = main.querySelector(`${TAB_SELECTOR}[aria-current="page"]`);
    if (activeTab instanceof HTMLElement) {
      activeTab.focus({ preventScroll: true });
    } else {
      main.setAttribute('tabindex', '-1');
      main.focus({ preventScroll: true });
    }
  } catch {
    window.location.assign(url);
  } finally {
    if (currentNavigation === navigationId) main.removeAttribute('aria-busy');
  }
}

/**
 * @param {MouseEvent} event
 * @returns {HTMLAnchorElement | null}
 */
function getTabFromEvent(event) {
  if (!(event.target instanceof Element)) return null;

  const link = event.target.closest(TAB_SELECTOR);
  if (!(link instanceof HTMLAnchorElement)) return null;
  if (link.origin !== window.location.origin) return null;

  return link;
}

/**
 * @param {MouseEvent} event
 */
function handleClick(event) {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

  const link = getTabFromEvent(event);
  if (!link) return;

  event.preventDefault();
  if (link.getAttribute('aria-current') === 'page') return;

  navigate(link.href, { push: true });
}

/**
 * @param {MouseEvent | FocusEvent} event
 */
function handlePrefetch(event) {
  const link = getTabFromEvent(/** @type {MouseEvent} */ (event));
  if (!link || link.getAttribute('aria-current') === 'page') return;

  loadPage(link.href).catch(() => {});
}

function handlePopState() {
  if (window.location.pathname === renderedPath) return;
  navigate(window.location.href, { push: false });
}

if (!window.Shopify?.designMode) {
  window.history.replaceState({ ...window.history.state, roleTabs: true }, '');

  document.addEventListener('click', handleClick);
  document.addEventListener('pointerover', handlePrefetch, { passive: true });
  document.addEventListener('focusin', handlePrefetch);
  window.addEventListener('popstate', handlePopState);
}

/**
 * @typedef {object} RoleTabsRefs
 * @property {HTMLElement} [list]
 * @property {HTMLElement} [active]
 */

/**
 * @extends {Component<RoleTabsRefs>}
 */
class RoleTabs extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#revealActive();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#revealActive();
  }

  #revealActive() {
    const { list, active } = this.refs;
    if (!list || !active || list.scrollWidth <= list.clientWidth) return;

    list.scrollLeft = active.offsetLeft - (list.clientWidth - active.offsetWidth) / 2;
  }
}

if (!customElements.get('role-tabs')) {
  customElements.define('role-tabs', RoleTabs);
}
